import { useCallback, useMemo, useState } from "react";
import { BulkUploadRepositoryImpl } from "../../data/repositories/BulkUploadRepositoryImpl";
import { DownloadCustomerTemplateUseCase } from "../../domain/use-cases/DownloadCustomerTemplateUseCase";
import { DownloadVendorTemplateUseCase } from "../../domain/use-cases/DownloadVendorTemplateUseCase";
import { DownloadGlTemplateUseCase } from "../../domain/use-cases/DownloadGlTemplateUseCase";
import { UploadCustomersBulkUseCase } from "../../domain/use-cases/UploadCustomersBulkUseCase";
import { UploadVendorsBulkUseCase } from "../../domain/use-cases/UploadVendorsBulkUseCase";
import { UploadGlsBulkUseCase } from "../../domain/use-cases/UploadGlsBulkUseCase";
import type { BulkUploadResultEntity } from "../../domain/entities/BulkUploadEntity";
import type { ChangeDetails } from "../../../../shared/utils/reviewWorkflow";
import {
  BulkVerifyForReviewError,
  type BulkVerifyForReviewFailure,
} from "../../data/api/bulkUploadApi";

export type BulkUploadKind = "customers" | "vendors" | "gls";

export function useBulkUpload(kind: BulkUploadKind) {
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [isUploading, setIsUploading] = useState(false);
  const [isDownloading, setIsDownloading] = useState(false);
  const [result, setResult] = useState<BulkUploadResultEntity | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [verifyFailures, setVerifyFailures] = useState<
    BulkVerifyForReviewFailure[]
  >([]);

  const useCases = useMemo(() => {
    const repository = new BulkUploadRepositoryImpl();
    return {
      downloadCustomer: new DownloadCustomerTemplateUseCase(repository),
      downloadVendor: new DownloadVendorTemplateUseCase(repository),
      downloadGl: new DownloadGlTemplateUseCase(repository),
      uploadCustomers: new UploadCustomersBulkUseCase(repository),
      uploadVendors: new UploadVendorsBulkUseCase(repository),
      uploadGls: new UploadGlsBulkUseCase(repository),
      repository,
    };
  }, []);

  const handleDownloadTemplate = useCallback(async () => {
    setError(null);
    setIsDownloading(true);
    try {
      if (kind === "customers") {
        await useCases.downloadCustomer.execute();
      } else if (kind === "vendors") {
        await useCases.downloadVendor.execute();
      } else {
        await useCases.downloadGl.execute();
      }
    } catch (e) {
      const msg = e instanceof Error ? e.message : "Download failed";
      setError(msg);
      throw e;
    } finally {
      setIsDownloading(false);
    }
  }, [kind, useCases]);

  const handleUpload = useCallback(async () => {
    if (!selectedFile) return;
    setError(null);
    setVerifyFailures([]);
    setResult(null);
    setIsUploading(true);
    try {
      const entity =
        kind === "customers"
          ? await useCases.uploadCustomers.execute(selectedFile)
          : kind === "vendors"
            ? await useCases.uploadVendors.execute(selectedFile)
            : await useCases.uploadGls.execute(selectedFile);
      setResult(entity);
      return entity;
    } catch (e) {
      const msg = e instanceof Error ? e.message : "Upload failed";
      setError(msg);
      throw e;
    } finally {
      setIsUploading(false);
    }
  }, [kind, selectedFile, useCases]);

  const verifyForReview = useCallback(async (): Promise<ChangeDetails> => {
    if (!selectedFile) {
      throw new Error("No file selected");
    }
    setError(null);
    setVerifyFailures([]);
    setResult(null);
    setIsUploading(true);
    try {
      const cd =
        kind === "customers"
          ? await useCases.repository.verifyCustomersForReview(selectedFile)
          : kind === "vendors"
            ? await useCases.repository.verifyVendorsForReview(selectedFile)
            : await useCases.repository.verifyGlsForReview(selectedFile);
      return cd;
    } catch (e) {
      if (e instanceof BulkVerifyForReviewError) {
        setVerifyFailures(e.invalidRows);
        setError(e.message);
        throw e;
      }
      const msg = e instanceof Error ? e.message : "Validation failed";
      setError(msg);
      throw e;
    } finally {
      setIsUploading(false);
    }
  }, [kind, selectedFile, useCases.repository]);

  const reset = useCallback(() => {
    setSelectedFile(null);
    setResult(null);
    setError(null);
    setVerifyFailures([]);
  }, []);

  const selectFile = useCallback((file: File | null) => {
    setError(null);
    setResult(null);
    setVerifyFailures([]);
    setSelectedFile(file);
  }, []);

  return {
    selectedFile,
    isUploading,
    isDownloading,
    result,
    error,
    verifyFailures,
    selectFile,
    downloadTemplate: handleDownloadTemplate,
    upload: handleUpload,
    verifyForReview,
    reset,
  };
}
